import { Helmet } from 'react-helmet-async'

const SITE_URL = 'https://sycek.io'

interface SEOHeadProps {
  title?: string
  description?: string
  /** Accepted but intentionally unused — meta[name=keywords] has been ignored
   * by Google since 2009, and writing one just telegraphs exact keyword
   * targets to competitors. Kept as a prop so none of the ~25 existing call
   * sites need editing. */
  keywords?: string
  ogImage?: string
  canonical?: string
  ogType?: string
  jsonLd?: Record<string, any>
}

/**
 * Declarative per-route <head> management via react-helmet-async — replaces
 * the previous imperative document.querySelector/createElement DOM mutation.
 * This is what makes per-route metadata capturable at build time (see
 * scripts/prerender.mjs): Helmet collects the tags rendered by whichever
 * page is active during a server-side renderToString() pass, so each
 * prerendered route gets its own real <title>/description/OG tags instead
 * of the generic index.html shell every route used to serve to crawlers
 * that don't execute JS.
 */
export function SEOHead({
  title = "Sycek OSINT - Advanced Cyber Intelligence & OSINT Platform",
  description = "Master advanced OSINT and cyber intelligence with Sycek's AI-powered platform. Sycek Profiler (breach intelligence), GEOINT, SOCINT, threat detection, and comprehensive digital forensics tools.",
  ogImage = "/logo.jpeg",
  canonical,
  ogType = "website",
  jsonLd,
}: SEOHeadProps) {
  const absoluteOgImage = ogImage ? (ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`) : undefined
  const absoluteCanonical = canonical ? (canonical.startsWith('http') ? canonical : `${SITE_URL}${canonical}`) : undefined

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      {absoluteOgImage && <meta property="og:image" content={absoluteOgImage} />}
      {absoluteCanonical && <meta property="og:url" content={absoluteCanonical} />}

      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {absoluteOgImage && <meta name="twitter:image" content={absoluteOgImage} />}

      {absoluteCanonical && <link rel="canonical" href={absoluteCanonical} />}

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", ...jsonLd })}
        </script>
      )}
    </Helmet>
  )
}
